import React, { Suspense, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { motion, useTransform, useScroll } from "framer-motion";
import { avatar1, avatar2, avatar4, avatar5, bgStars } from "../../../assets";
import {
  MoveUpRight,
  ArrowRight,
  ArrowUpRight,
  CircleUserRound,
  CheckCircle,
} from "lucide-react";
import Header from "../../common/Header";
import {
  scrollVariantsLeft,
  scrollVariantsRight,
  upAndDown,
  downAndUp,
} from "../../../constants/TextAnimation";
import GlobeDemo from "../../common/GlobeDemo";
import { SparklesCore } from "../../ui/sparkles";
import { HoverBorderGradient } from "../../ui/hover-border-gradient";
import { GlowingEffectDemo } from "../../animation/GlowingEffectDemo";
import { CardHoverEffectDemo } from "../../animation/CardHoverEffectDemo";
// import GoldenCard from "./GoldenCard";

const testimonials = [
  {
    text: "Scan Consulting Group has delivered excellent accounting and business advice for over 4 years. Wahaj and his team have helped me grow from self-employed to a small business owner. Highly recommend!",
    imageSrc: avatar1,
    name: "SamSara Speaks",
    username: "@SamSaraSpeaks123",
  },
  {
    text: "Wahaj is knowledgeable, polite, and trustworthy. He helped us with a small business loan, and I was also impressed by Sadia’s communication. Highly recommended for personal and business finance needs. ",
    imageSrc: avatar2,
    name: "Puneet Kaur Khurmi",
    username: "@puneetkaurkhurmi",
  },
  {
    text: "Scan Consulting helped with my complex personal taxes and now supports my small business. Wahaj and his team are responsive, reliable, and great for accounting and financial advice.",
    imageSrc: avatar4,
    name: "Tara McArthy",
    username: "@taramcarthy",
  },
  {
    text: "Scan Consulting filed my taxes with great care. I fully trust them for all my accounting needs and will use their services again.",
    imageSrc: avatar5,
    name: "Lucas Provenzano",
    username: "@lucasprovenzano",
  },
  {
    text: "Scan Consulting Group has delivered excellent accounting and business advice for over 4 years. Wahaj and his team have helped me grow from self-employed to a small business owner. Highly recommend!",
    imageSrc: avatar1,
    name: "SamSara Speaks",
    username: "@SamSaraSpeaks123",
  },
  {
    text: "Wahaj is knowledgeable, polite, and trustworthy. He helped us with a small business loan, and I was also impressed by Sadia’s communication. Highly recommended for personal and business finance needs. ",
    imageSrc: avatar2,
    name: "Puneet Kaur Khurmi",
    username: "@puneetkaurkhurmi",
  },
  {
    text: "Scan Consulting helped with my complex personal taxes and now supports my small business. Wahaj and his team are responsive, reliable, and great for accounting and financial advice.",
    imageSrc: avatar4,
    name: "Tara McArthy",
    username: "@taramcarthy",
  },
  {
    text: "Scan Consulting filed my taxes with great care. I fully trust them for all my accounting needs and will use their services again.",
    imageSrc: avatar5,
    name: "Lucas Provenzano",
    username: "@lucasprovenzano",
  },
  {
    text: "Scan Consulting Group has delivered excellent accounting and business advice for over 4 years. Wahaj and his team have helped me grow from self-employed to a small business owner. Highly recommend!",
    imageSrc: avatar1,
    name: "SamSara Speaks",
    username: "@SamSaraSpeaks123",
  },
];

const firstColumn = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn = testimonials.slice(6, 9);

// testimonials map
const TestimonialsColumn = (props) => {
  // console.log(props);
  const { classNames, testimonials, duration } = props;
  return (
    <div className={classNames}>
      <motion.div
        animate={{ translateY: "-50%" }}
        transition={{
          duration: duration || 10,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6"
      >
        {[...new Array(2)].fill(0).map((_, index) => (
          <React.Fragment key={index}>
            {testimonials.map(({ text, imageSrc, name, username }) => (
              <div className="card bg-black" key={name}>
                <div className="text-sm">{text}</div>
                <div className="flex items-center gap-2 mt-5">
                  {/* <img
                    src={imageSrc}
                    alt={name}
                    width={40}
                    height={40}
                    className="h-10 w-10 rounded-full"
                  /> */}
                  <CircleUserRound className="h-10 w-10 rounded-full p-2 bg-gold/10 text-gold" />
                  <div className="flex flex-col">
                    <div className="font-medium tracking-tight leading-5">
                      {name}
                    </div>
                    <div className="leading-5 tracking-tight">{username}</div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const imageRef = useRef(null);
  const secondSectionRef = useRef(null);
  const homeRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: homeRef,
    offset: ["start end", "end start"],
  });

  const backgroundPositionY = useTransform(
    scrollYProgress,
    [0, 1],
    [-300, 300]
  );

  const scrollToNextSection = () => {
    const offset = 100;
    const sectionTop =
      secondSectionRef.current.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: sectionTop - offset,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const image = imageRef.current;

    if (!image) return;

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: "section:nth-child(2)",
        start: "top bottom",
        endTrigger: "section:nth-child(5)",
        end: "bottom center",
        scrub: 3,
        toggleActions: "restart pause reverse pause", // Reverse on scroll back
      },
    });

    let prevY = 0; // Track the Y position incrementally

    // Define animations in sequence
    timeline
      .to(image, {
        x: 600,
        y: -120,
        opacity: 1,
        // rotation: 270,
        // width: "16%",
        ease: "power1.inOut",
      })
      .to(image, {
        x: -450, //350
        y: prevY + 100,
        rotation: 0,
        opacity: 0.45,
        // width: "10%",
        ease: "power1.inOut",
        onStart: () => {
          prevY += 100; // Update previous Y
        },
      })
      .to(image, {
        x: 750,
        y: prevY + 200,
        // scale: 2,
        // rotation: 360,
        opacity: 1,
        // width: "5%",
        ease: "power1.inOut",
        onStart: () => {
          prevY += 100; // Update previous Y
        },
      });

    // Cleanup ScrollTrigger instances on component unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <motion.div
      ref={homeRef}
      className="relative"
      style={{
        backgroundImage: `url(${bgStars})`,
        backgroundPositionY,
        zIndex: 0,
        // pointerEvents: "none",
      }}
      animate={{ backgroundPositionX: "100%" }}
      transition={{
        repeat: Infinity,
        ease: "linear",
        duration: 60,
      }}
    >
      {/* Hero section with video */}
      <section className="relative w-full min-h-[62vh pt-10 xl:pt-0 lg:min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="container">
          <div className="grid lg:grid-cols-2">
            <div className="my-auto">
              <motion.div
                variants={scrollVariantsLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
                className="relative max-w-2xl"
              >
                <h2 className="text-2xl font-extrabold relative z-[0] pt-3 md:pt-14 !leading-normal md:text-3xl lg:text-[2.3rem] CustomFont">
                  Scan Global Is Your Partner For Safe, Smart Financial Growth.
                </h2>
                {/* stars */}
                <div className="absolute top-34 -left-2">
                  <div className="w-[26rem] h-5 relative">
                    {/* Gradients */}
                    <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-[2px] w-3/4 blur-sm" />
                    <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-px w-3/4" />
                    <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-[5px] w-1/4 blur-sm" />
                    <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-px w-1/4" />

                    {/* Core component */}
                    <SparklesCore
                      background="transparent"
                      minSize={0.4}
                      maxSize={1}
                      particleDensity={1200}
                      className="w-full h-full"
                      particleColor="#FFFFFF"
                    />

                    {/* Radial Gradient to prevent sharp edges */}
                    <div className="absolute inset-0 w-full h-full bg-black [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]"></div>
                  </div>
                </div>

                <p className="mt-4 mb-10 text-lg leading-relaxed text-gray-300 ">
                  From startup advice and capital raising to global expansion
                  and real estate strategy - We help you protect your money, pay
                  less tax, and grow your business globally.
                </p>
                {/* <Link
                  to="/contact"
                  className="bg-white/10 text-white font-semibold py-1.5 ps-5 pe-1.5 rounded-full hover:opacity-80 transition duration-200 inline-flex items-center gap-4 leading-normal cursor-pointer"
                >
                  Get Started
                  <span className="bg-white text-slate-900 p-2.5 rounded-full">
                    <ArrowUpRight size={18} />
                  </span>
                </Link> */}

                {/* <Link to="/contact">
                  <HoverBorderGradient
                    containerClassName="rounded-full"
                    as="button"
                    className="group bg-white/5 backdrop-blur-md text-white font-semibold py-1.5 ps-5 pe-1.5 rounded-full hover:opacity-80 transition duration-200 inline-flex items-center gap-4 leading-normal space-x-2"
                  >
                    Get Started
                    <span className="bg-white text-slate-900 p-2.5 rounded-full transition-transform duration-300 group-hover:rotate-[45deg]">
                      <ArrowUpRight size={18} />
                    </span>
                  </HoverBorderGradient>
                </Link> */}
                <div className="inline-block">
                  <Link
                    to="/contact"
                    className="flex justify-center gap-2 items-center mx-auto shadow-xl text-base bg-gradient text-slate-900 backdrop-blur-md lg:font-semibold isolation-auto border-gray-50 before:absolute before:w-full before:transition-all before:duration-700 before:hover:w-full before:-left-full before:hover:left-0 before:rounded-full before:bg-gold hover:text-slate-900 hover:border-gold before:-z-10 before:aspect-square before:hover:scale-150 before:hover:duration-700 relative z-10 ps-4 pe-1 py-1 overflow-hidden border-2 rounded-full group"
                  >
                    Get Started
                    <svg
                      className="w-8 h-8 justify-end group-hover:rotate-90 group-hover:bg-gray-50 ease-linear duration-300  bg-black text-white p-2 rounded-full group-hover:border-none rotate-45"
                      viewBox="0 0 16 19"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 18C7 18.5523 7.44772 19 8 19C8.55228 19 9 18.5523 9 18H7ZM8.70711 0.292893C8.31658 -0.0976311 7.68342 -0.0976311 7.29289 0.292893L0.928932 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292893ZM9 18L9 1H7L7 18H9Z"
                        className="fill-white group-hover:fill-gray-800"
                      />
                    </svg>
                  </Link>
                </div>
                {/* <div className="block md:hidden mt-6 flex flex-wrap items-center gap-4 justify-center mb-6">
                  <Link
                    to="/solutions"
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition duration-200 w-[130px]"
                  >
                    <img
                      src="/images/solutions/mini-logo.png"
                      alt="Solutions"
                      className="w-9 h-9 object-cover"
                    />
                    <span className="text-sm font-medium">Advisory</span>
                  </Link>

                  <Link
                    to="/realty"
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition duration-200 w-[130px]"
                  >
                    <img
                      src="/images/realty/realty-logo.png"
                      alt="Realty"
                      className="w-9 h-9 object-cover"
                    />
                    <span className="text-sm font-medium">Realty</span>
                  </Link>
                </div> */}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* brand animation */}
      <div ref={secondSectionRef} className="pb-8 pt-5 md:pb-12">
        <div className="container">
          <div className="flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black,transparent)]">
            <motion.div
              className="flex gap-14 flex-none pr-14"
              animate={{
                translateX: "-50%",
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
                repeatType: "loop",
              }}
            >
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Global Expansion Strategies</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Tax Optimization & Wealth Protection</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Business Growth & Advisory Services</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Mergers & Acquisitions Support</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Raising Capital & Working Capital Solutions</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Professional Accounting & Tax Advice</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Strategic Real Estate Advisory</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Investor Readiness & Funding Support</p>
              </div>
              <div className="flex items-center gap-2 text-md">
                <img
                  src="/images/daimond-logo.png"
                  alt="logo"
                  className="object-cover w-8 h-8"
                />
                <p>Startup & SME Consulting</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* features section */}
      <section className="py-8 sm:py-16 lg:py-18" id="our-expertise">
        <div className="px-4 mx-auto sm:px-6 lg:px-8">
          <motion.div
            variants={scrollVariantsLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
              Our Expertise
            </h2>
            <p className="max-w-lg mx-auto mt-1 text-lg leading-relaxed text-gray-400">
              Unlocking growth with strategic capabilities that make a
              difference.
            </p>
          </motion.div>

          {/* <div className="relative mt-12 lg:mt-20">
            <div className="absolute inset-x-0 hidden xl:px-44 top-2 md:block md:px-20 lg:px-28">
              <img
                className="w-full"
                src="https://cdn.rareblocks.xyz/collection/celebration/images/steps/2/curved-dotted-line.svg"
                alt=""
              />
            </div>

            <div className="relative grid grid-cols-1 text-center gap-y-12 md:grid-cols-3 gap-x-12">
              <div>
                <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-yellow-300 rounded-full shadow overflow-hidden">
                  <img
                    src={worldWide}
                    alt="gif"
                    className="object-cover w-12 h-12"
                  />
                </div>
                <motion.h3
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-6 text-xl font-semibold leading-tight text-white md:mt-10"
                >
                  Mergers & Acquisitions (M&A)
                </motion.h3>
                <motion.p
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-4 text-base text-gray-400"
                >
                  Navigating the complexities of mergers and acquisitions to
                  maximize value
                </motion.p>
              </div>

              <div>
                <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-yellow-300 rounded-full shadow overflow-hidden">
                  <img
                    src={globe}
                    alt="gif"
                    className="object-cover w-12 h-12"
                  />
                </div>
                <motion.h3
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-6 text-xl font-semibold leading-tight text-white md:mt-10"
                >
                  Finance & Business Advisory
                </motion.h3>
                <motion.p
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-4 text-base text-gray-400"
                >
                  Enabling businesses to streamline operations and achieve
                  financial excellence
                </motion.p>
              </div>

              <div>
                <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-yellow-300 rounded-full shadow overflow-hidden">
                  <img
                    src={startUp}
                    alt="gif"
                    className="object-cover w-12 h-12"
                  />
                </div>
                <motion.h3
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-6 text-xl font-semibold leading-tight text-white md:mt-10"
                >
                  Strategic Investment Advisory
                </motion.h3>
                <motion.p
                  variants={scrollVariantsLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-4 text-base text-gray-400"
                >
                  Delivering investment strategies in thriving global markets
                </motion.p>
              </div>
            </div>
          </div> */}
          <GlowingEffectDemo />
        </div>
      </section>

      {/* <section className="py-8 md:py-16 lg:py-18">
        <GoldenCard />
      </section> */}

      {/* Oue Network */}
      <section className="py-6 sm:pt-16 lg:pt-18 ">
        <motion.div
          variants={scrollVariantsLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          className="max-w-2xl mx-auto text-center"
        >
          <h2 className="text-3xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
            Our Network
          </h2>
          <p className="max-w-lg mx-auto mt-1 text-base lg:text-lg leading-relaxed text-gray-300">
            Driving innovation and industry-wide success through tailored
            solutions and a powerful integrated network
          </p>
        </motion.div>
        <div className="container">
          {/* <div className="grid md:grid-cols-2 gap-4 md:gap-8 xl:gap-20 md:items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={upAndDown}
              viewport={{ amount: 0.3 }}
              exit="exit"
            >
              <h1
                className="block text-3xl font-bold sm:text-4xl lg:text-5xl lg:leading-tight"
                style={{ fontFamily: "monospace" }}
              >
                Your Partner in {""}
                <span className="text-yellow-800">Growth!</span>
              </h1>
              <p className="mt-3 text-lg text-gray-400">
                At Scan Global Holdings, we enable businesses to thrive by
                leveraging interconnected expertise across finance, business
                consulting, and tax advisory. Our mission is to simplify
                complexity and deliver solutions that drive growth and
                innovation.
              </p>

              <div className="mt-7 gap-3 w-full flex items-center">
                <a
                  className="py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-yellow-700 text-white hover:bg-yellow-800 focus:outline-hidden focus:bg-yellow-800 disabled:opacity-50 disabled:pointer-events-none"
                  href="#"
                >
                  Get started
                  <svg
                    className="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </a>
                <a
                  className="py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xs hover:bg-gray-200 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  href="#"
                >
                  Talk to an Expert <MoveUpRight size={16} />
                </a>
              </div>

              <div className="mt-6 lg:mt-10 grid grid-cols-2 gap-x-5">
                <div className="py-5">
                  <div className="flex gap-x-1">
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>

                  <p className="mt-3 text-sm text-gray-400">
                    <span className="font-bold">4.6</span> /5 - from 12k reviews
                  </p>

                  <div className="mt-5">
                    <svg
                      className="h-auto w-16 text-gray-400"
                      width="80"
                      height="27"
                      viewBox="0 0 80 27"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20.558 9.74046H11.576V12.3752H17.9632C17.6438 16.0878 14.5301 17.7245 11.6159 17.7245C7.86341 17.7245 4.58995 14.7704 4.58995 10.6586C4.58995 6.62669 7.70373 3.51291 11.6159 3.51291C14.6498 3.51291 16.4063 5.42908 16.4063 5.42908L18.2426 3.51291C18.2426 3.51291 15.8474 0.878184 11.4961 0.878184C5.94724 0.838264 1.67578 5.50892 1.67578 10.5788C1.67578 15.5289 5.70772 20.3592 11.6558 20.3592C16.8854 20.3592 20.7177 16.8063 20.7177 11.4969C20.7177 10.3792 20.558 9.74046 20.558 9.74046Z"
                        fill="currentColor"
                      />
                      <path
                        d="M27.8621 7.78442C24.1894 7.78442 21.5547 10.6587 21.5547 14.012C21.5547 17.4451 24.1096 20.3593 27.9419 20.3593C31.415 20.3593 34.2094 17.7645 34.2094 14.0918C34.1695 9.94011 30.896 7.78442 27.8621 7.78442ZM27.902 10.2994C29.6984 10.2994 31.415 11.7764 31.415 14.0918C31.415 16.4072 29.7383 17.8842 27.902 17.8842C25.906 17.8842 24.3491 16.2874 24.3491 14.0519C24.3092 11.8962 25.8661 10.2994 27.902 10.2994Z"
                        fill="currentColor"
                      />
                      <path
                        d="M41.5964 7.78442C37.9238 7.78442 35.2891 10.6587 35.2891 14.012C35.2891 17.4451 37.844 20.3593 41.6763 20.3593C45.1493 20.3593 47.9438 17.7645 47.9438 14.0918C47.9038 9.94011 44.6304 7.78442 41.5964 7.78442ZM41.6364 10.2994C43.4328 10.2994 45.1493 11.7764 45.1493 14.0918C45.1493 16.4072 43.4727 17.8842 41.6364 17.8842C39.6404 17.8842 38.0835 16.2874 38.0835 14.0519C38.0436 11.8962 39.6004 10.2994 41.6364 10.2994Z"
                        fill="currentColor"
                      />
                      <path
                        d="M55.0475 7.82434C51.6543 7.82434 49.0195 10.7784 49.0195 14.0918C49.0195 17.8443 52.0934 20.3992 54.9676 20.3992C56.764 20.3992 57.6822 19.7205 58.4407 18.8822V20.1198C58.4407 22.2754 57.1233 23.5928 55.1273 23.5928C53.2111 23.5928 52.2531 22.1557 51.8938 21.3573L49.4587 22.3553C50.297 24.1517 52.0135 26.0279 55.0874 26.0279C58.4407 26.0279 60.9956 23.9122 60.9956 19.481V8.18362H58.3608V9.26147C57.6423 8.38322 56.5245 7.82434 55.0475 7.82434ZM55.287 10.2994C56.9237 10.2994 58.6403 11.7365 58.6403 14.1317C58.6403 16.6068 56.9636 17.9241 55.2471 17.9241C53.4507 17.9241 51.774 16.4471 51.774 14.1716C51.8139 11.6966 53.5305 10.2994 55.287 10.2994Z"
                        fill="currentColor"
                      />
                      <path
                        d="M72.8136 7.78442C69.62 7.78442 66.9453 10.2994 66.9453 14.0519C66.9453 18.004 69.9393 20.3593 73.093 20.3593C75.7278 20.3593 77.4044 18.8822 78.3625 17.6048L76.1669 16.1277C75.608 17.006 74.6499 17.8443 73.093 17.8443C71.3365 17.8443 70.5381 16.8862 70.0192 15.9281L78.4423 12.4152L78.0032 11.3772C77.1649 9.46107 75.2886 7.78442 72.8136 7.78442ZM72.8934 10.2196C74.0511 10.2196 74.8495 10.8184 75.2487 11.5768L69.6599 13.9321C69.3405 12.0958 71.097 10.2196 72.8934 10.2196Z"
                        fill="currentColor"
                      />
                      <path
                        d="M62.9531 19.9999H65.7076V1.47693H62.9531V19.9999Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>

                <div className="py-5">
                  <div className="flex gap-x-1">
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27.0352 1.6307L33.9181 16.3633C34.2173 16.6768 34.5166 16.9903 34.8158 16.9903L50.0779 19.1845C50.9757 19.1845 51.275 20.4383 50.6764 21.0652L39.604 32.3498C39.3047 32.6632 39.3047 32.9767 39.3047 33.2901L41.998 49.2766C42.2973 50.217 41.1002 50.8439 40.5017 50.5304L26.4367 43.3208C26.1375 43.3208 25.8382 43.3208 25.539 43.3208L11.7732 50.8439C10.8754 51.1573 9.97763 50.5304 10.2769 49.59L12.9702 33.6036C12.9702 33.2901 12.9702 32.9767 12.671 32.6632L1.29923 21.0652C0.700724 20.4383 0.999979 19.4979 1.89775 19.4979L17.1598 17.3037C17.459 17.3037 17.7583 16.9903 18.0575 16.6768L24.9404 1.6307C25.539 0.69032 26.736 0.69032 27.0352 1.6307Z"
                        fill="currentColor"
                      />
                    </svg>
                    <svg
                      className="size-4 text-gray-400"
                      width="51"
                      height="51"
                      viewBox="0 0 51 51"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M49.6867 20.0305C50.2889 19.3946 49.9878 18.1228 49.0846 18.1228L33.7306 15.8972C33.4296 15.8972 33.1285 15.8972 32.8275 15.2613L25.9032 0.317944C25.6021 0 25.3011 0 25 0V42.6046C25 42.6046 25.3011 42.6046 25.6021 42.6046L39.7518 49.9173C40.3539 50.2352 41.5581 49.5994 41.2571 48.6455L38.5476 32.4303C38.5476 32.1124 38.5476 31.7944 38.8486 31.4765L49.6867 20.0305Z"
                        fill="transparent"
                      />
                      <path
                        d="M0.313299 20.0305C-0.288914 19.3946 0.0122427 18.1228 0.915411 18.1228L16.2694 15.8972C16.5704 15.8972 16.8715 15.8972 17.1725 15.2613L24.0968 0.317944C24.3979 0 24.6989 0 25 0V42.6046C25 42.6046 24.6989 42.6046 24.3979 42.6046L10.2482 49.9173C9.64609 50.2352 8.44187 49.5994 8.74292 48.6455L11.4524 32.4303C11.4524 32.1124 11.4524 31.7944 11.1514 31.4765L0.313299 20.0305Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>

                  <p className="mt-3 text-sm text-gray-400">
                    <span className="font-bold">4.8</span> /5 - from 5k reviews
                  </p>

                  <div className="mt-5">
                    <svg
                      className="h-auto w-16 text-gray-400"
                      width="110"
                      height="28"
                      viewBox="0 0 110 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M66.6601 8.35107C64.8995 8.35107 63.5167 8.72875 62.1331 9.48265C62.1331 5.4582 62.1331 1.81143 62.2594 0.554199L53.8321 2.06273V2.81736L54.7124 2.94301C55.8433 3.19431 56.2224 3.82257 56.4715 5.33255C56.725 8.35107 56.5979 24.4496 56.4715 27.0912C58.7354 27.5945 61.1257 27.9722 63.5159 27.9722C70.1819 27.9722 74.2064 23.8213 74.2064 17.281C74.2064 12.1249 70.9366 8.35107 66.6601 8.35107ZM63.7672 26.5878C63.2639 26.5878 62.6342 26.5878 62.258 26.4629C62.1316 24.7023 62.0067 17.281 62.1316 10.7413C62.8862 10.4893 63.3888 10.3637 64.0185 10.3637C66.7872 10.3637 68.2965 13.6335 68.2965 17.6572C68.2957 22.6898 66.4088 26.5878 63.7672 26.5878ZM22.1363 1.0568H0V2.18838L1.25796 2.31403C2.89214 2.56533 3.52184 3.57127 3.77242 5.9608C4.15082 10.4886 4.02445 18.6646 3.77242 22.5619C3.52112 24.9522 2.89287 26.0845 1.25796 26.2087L0 26.4615V27.4674H14.2123V26.4615L12.703 26.2087C11.0681 26.0838 10.4392 24.9522 10.1879 22.5619C10.0615 20.9263 9.93583 18.2847 9.93583 15.0156L12.9543 15.1413C14.8413 15.1413 15.7208 16.6505 16.0985 18.7881H17.2308V9.86106H16.0985C15.7201 11.9993 14.8413 13.5078 12.9543 13.5078L9.93655 13.6342C9.93655 9.35773 10.0622 5.33328 10.1886 2.94374H14.59C17.9869 2.94374 19.7475 5.08125 21.0047 8.85513L22.2626 8.47745L22.1363 1.0568Z"
                        fill="currentColor"
                      />
                      <path
                        d="M29.3053 8.09998C35.5944 8.09998 38.7385 12.3764 38.7385 18.0358C38.7385 23.4439 35.2167 27.9731 28.9276 27.9731C22.6393 27.9731 19.4951 23.6959 19.4951 18.0358C19.4951 12.6277 23.0162 8.09998 29.3053 8.09998ZM28.9276 9.35793C26.1604 9.35793 25.4058 13.1311 25.4058 18.0358C25.4058 22.8149 26.6637 26.7137 29.1796 26.7137C32.0703 26.7137 32.8264 22.9405 32.8264 18.0358C32.8264 13.2567 31.5699 9.35793 28.9276 9.35793ZM75.8403 18.1622C75.8403 13.0054 79.1101 8.09998 85.5248 8.09998C90.8057 8.09998 93.3224 11.9995 93.3224 17.1555H81.6253C81.4989 21.8089 83.7628 25.2051 88.2913 25.2051C90.3038 25.2051 91.3098 24.7033 92.5685 23.8223L93.0703 24.4505C91.8124 26.2111 89.0459 27.9731 85.5248 27.9731C79.8647 27.9724 75.8403 23.9479 75.8403 18.1622ZM81.6253 15.7726L87.5366 15.6463C87.5366 13.1311 87.159 9.35793 85.0214 9.35793C82.8839 9.35793 81.7502 12.8791 81.6253 15.7726ZM108.291 9.10663C106.782 8.47693 104.77 8.09998 102.506 8.09998C97.8538 8.09998 94.9594 10.8665 94.9594 14.137C94.9594 17.4075 97.0955 18.7904 100.118 19.7971C103.261 20.9279 104.142 21.8089 104.142 23.3182C104.142 24.8275 103.01 26.2103 100.997 26.2103C98.6084 26.2103 96.8464 24.8275 95.4635 21.0536L94.5825 21.3063L94.7089 26.84C96.2181 27.4683 98.9846 27.9724 101.375 27.9724C106.28 27.9724 109.425 25.4557 109.425 21.5576C109.425 18.9161 108.041 17.4075 104.771 16.1489C101.249 14.766 99.992 13.8857 99.992 12.2501C99.992 10.6152 101.126 9.48286 102.635 9.48286C104.897 9.48286 106.407 10.8665 107.54 14.2627L108.42 14.0114L108.291 9.10663ZM55.0883 8.6033C52.9508 7.3468 49.1769 7.97433 47.1651 12.5028L47.29 8.1007L38.8642 9.73561V10.4902L39.7444 10.6159C40.8775 10.7423 41.3794 11.3705 41.5057 13.0062C41.757 16.0247 41.6314 21.3078 41.5057 23.9486C41.3794 25.4564 40.8775 26.2111 39.7444 26.3374L38.8642 26.4638V27.4697H50.5606V26.4638L49.0513 26.3374C47.7941 26.2111 47.4164 25.4564 47.29 23.9486C47.0387 21.5584 47.0387 16.7793 47.1651 13.7608C47.7933 12.8798 50.5606 12.1259 53.0757 13.7608L55.0883 8.6033Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative ms-4"
              initial="hidden"
              whileInView="visible"
              variants={downAndUp}
              viewport={{ amount: 0.3 }}
              exit="exit"
            >
              <img
                className="w-full rounded-md object-cover relative z-10"
                src={team}
                alt="Hero Image"
                style={{ height: "500px" }}
              />
              <div className="absolute inset-0 -z-1 bg-gradient-to-tr from-yellow-200 via-white/0 to-white/0 size-full rounded-md mt-4 -mb-4 me-4 -ms-4 lg:mt-6 lg:-mb-6 lg:me-6 lg:-ms-6"></div>

              <div className="absolute bottom-0 start-0">
                <svg
                  className="w-2/3 ms-auto h-auto text-black"
                  width="630"
                  height="451"
                  viewBox="0 0 630 451"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="531"
                    y="352"
                    width="99"
                    height="99"
                    fill="currentColor"
                  />
                  <rect
                    x="140"
                    y="352"
                    width="106"
                    height="99"
                    fill="currentColor"
                  />
                  <rect
                    x="482"
                    y="402"
                    width="64"
                    height="49"
                    fill="currentColor"
                  />
                  <rect
                    x="433"
                    y="402"
                    width="63"
                    height="49"
                    fill="currentColor"
                  />
                  <rect
                    x="384"
                    y="352"
                    width="49"
                    height="50"
                    fill="currentColor"
                  />
                  <rect
                    x="531"
                    y="328"
                    width="50"
                    height="50"
                    fill="currentColor"
                  />
                  <rect
                    x="99"
                    y="303"
                    width="49"
                    height="58"
                    fill="currentColor"
                  />
                  <rect
                    x="99"
                    y="352"
                    width="49"
                    height="50"
                    fill="currentColor"
                  />
                  <rect
                    x="99"
                    y="392"
                    width="49"
                    height="59"
                    fill="currentColor"
                  />
                  <rect
                    x="44"
                    y="402"
                    width="66"
                    height="49"
                    fill="currentColor"
                  />
                  <rect
                    x="234"
                    y="402"
                    width="62"
                    height="49"
                    fill="currentColor"
                  />
                  <rect
                    x="334"
                    y="303"
                    width="50"
                    height="49"
                    fill="currentColor"
                  />
                  <rect x="581" width="49" height="49" fill="currentColor" />
                  <rect x="581" width="49" height="64" fill="currentColor" />
                  <rect
                    x="482"
                    y="123"
                    width="49"
                    height="49"
                    fill="currentColor"
                  />
                  <rect
                    x="507"
                    y="124"
                    width="49"
                    height="24"
                    fill="currentColor"
                  />
                  <rect
                    x="531"
                    y="49"
                    width="99"
                    height="99"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </motion.div>
          </div> */}
          <CardHoverEffectDemo />
        </div>
      </section>

      {/* cta section */}
      {/* <section className="bg-blac 2xl:py-24 2xl:bg-gray-50 relative bg-gradient-to-tr from-yellow-100 via-transparent dark:from-yellow-950/50 dark:via-transparent">
        <div className="px-4 mx-auto bg-blac max-w-7xl sm:px-6 lg:px-8 2xl:rounded-xl">
          <div className="py-10 sm:py-16 lg:py-24 2xl:pl-24">
            <div className="grid items-center grid-cols-1 gap-y-8 lg:grid-cols-2 lg:gap-x-8 2xl:gap-x-20">
              <div className="lg:order-2 2xl:-mr-24">
                <img
                  className="w-full shadow-xl rounded-xl"
                  src="https://cdn.rareblocks.xyz/collection/celebration/images/cta/6/dashboard-screenshot.png"
                  alt=""
                />
              </div>

              <div className="lg:order-1">
                <h2
                  className="text-3xl font-bold leading-tight text-whit sm:text-4xl lg:text-5xl lg:leading-tight"
                  style={{ fontFamily: "monospace" }}
                >
                  
                  A Network of <br className="hidden xl:block" />
                  Expertise
                </h2>
                <p className="mt-3 text-lg text-gray-400">
                  Our integrated brands deliver solutions across industries and
                  regions. From finance advisory to strategic consulting, our
                  network of firms are empowered to create exceptional value.
                </p>

                <ul className="grid grid-cols-1 mt-4 sm:mt-10 sm:grid-cols-2 gap-x-10 xl:gap-x-16 gap-y-4 xl:gap-y-6">
                  <li className="flex items-center">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-yellow-700"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="ml-3 font-medium text-whit">
                      {" "}
                      120+ Coded Blocks{" "}
                    </span>
                  </li>

                  <li className="flex items-center">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-yellow-700"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="ml-3 font-medium text-whit">
                      {" "}
                      Free Icons Included{" "}
                    </span>
                  </li>

                  <li className="flex items-center">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-yellow-700"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="ml-3 font-medium text-whit">
                      {" "}
                      Made with TailwindCSS{" "}
                    </span>
                  </li>
                </ul>

                <div className="flex flex-col items-start mt-8 sm:space-x-4 sm:flex-row sm:items-center lg:mt-6">
                  <Link
                    href="#"
                    title=""
                    className="inline-flex items-center justify-center px-4 py-3 text-base font-semibold text-black transition-all duration-100 bg-yellow-300 border border-transparent hover:bg-yellow-400 focus:bg-yellow-400"
                    role="button"
                  >
                    <span className="p-1 bg-black text-white me-3">
                      <svg
                        className="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </span>
                    Download Now
                  </Link>

                  <Link
                    href="#"
                    title=""
                    className="inline-flex items-center justify-center px-4 py-3 mt-5 text-base font-semibold text-yellow-700 transition-all duration-200 bg-transparent border border-yellow-700 sm:mt-0 hover:bg-yellow-700 hover:text-white"
                    role="button"
                  >
                    {" "}
                    Explore Our Expertise{" "}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* aboutus */}
      <section className="py-3">
        {/* Hero */}
        <div className="relative overflow-hidden before:absolute before:top-0 before:start-1/2 before:bg-[url('/images/squared-bg-element.svg')] before:bg-no-repeat before:bg-top before:size-full before:-z-1 before:transform before:-translate-x-1/2 before:invert before:z-[-1]">
          <div className="max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {/* <div className="relative overflow-hidden before:absolute before:top-1/2 before:start-1/2 before:bg-[url('/images/lines.svg')] before:bg-no-repeat before:bg-center before:w-full before:h-96 before:-z-1 before:transform before:-translate-y-1/2 before:-translate-x-1/2"></div> */}
            {/* Announcement Banner */}
            <div className="flex justify-center">
              <a
                className="inline-flex items-center gap-x-2 bg-black border border-gray-200 text-xs text-gray-100 p-2 px-3 rounded-full transition hover:border-gray-300 focus:outline-hidden focus:border-gray-300"
                href="/about-us"
              >
                Explore the About Us
                <span className="flex items-center gap-x-1">
                  <span className="border-s border-gray-200 text-gold ps-2">
                    Explore
                  </span>
                  <svg
                    className="shrink-0 size-4 text-gold"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </a>
            </div>
            {/* End Announcement Banner */}

            {/* Title */}
            <div className="mt-5 max-w-xl text-center mx-auto">
              <h1 className="block font-bold text-gray-200 text-2xl md:text-4xl lg:text-5xl">
                Empowering Businesses Strategically
              </h1>
            </div>
            {/* End Title */}

            <div className="mt-5 max-w-3xl text-center mx-auto">
              <p className="text-lg text-gray-200">
                {/*&quot;*/}We build influential networks that drive growth and
                opportunities.{/*&quot;*/}
              </p>
            </div>

            {/* Buttons */}
            <div className="mt-8 gap-3 flex justify-center items-center">
              <Link
                to="/about-us"
                className="inline-flex justify-center items-center gap-x-3 text-center bg-secondary hover:opacity-80 transition duration-200  text-black text-sm lg:text-[1.1rem] leading-tight font-medium rounded-full py-2 px-4"
              >
                Explore more <ArrowRight size={20} />
              </Link>
              {/* <div className="inline-block bg-gradient-to-tl from-gold via-transparent to-secondary p-px rounded-xl mb-3">
                <div className="rounded-xl py-1.5 px-5 text-3xl font-bold md:text-4xl lg:text-2xl lg:leading-tight bg-neutral-900">
                  <div className="bg-clip-text flex gap-2 items-center">
                    Explore more <ArrowRight size={20} color="#fff" />
                  </div>
                </div>
              </div> */}
            </div>
            {/* End Buttons */}
          </div>
        </div>
        {/* End Hero */}
      </section>

      {/* testimonial */}
      <section className="py-10 relative z-10 lg:pt-30">
        <div className="container">
          <div className="section-heading">
            <div className="flex justify-center">
              <div className="tag">Testimonials</div>
            </div>
            <div className="max-w-[540px] mx-auto">
              <h2 className="section-title mt-3">What our users say</h2>
              <p className="section-description mt-5 text-gray-300">
                From intuitive design to powerful features, our app has become
                an essential tool for users around the world.
              </p>
            </div>
          </div>
          <div className="flex justify-center gap-6 mt-10 [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)] max-h-[740px] overflow-hidden">
            <TestimonialsColumn testimonials={firstColumn} duration={15} />
            <TestimonialsColumn
              testimonials={secondColumn}
              classNames="hidden md:block"
              duration={19}
            />
            <TestimonialsColumn
              testimonials={thirdColumn}
              classNames="hidden lg:block"
              duration={17}
            />
          </div>
        </div>
      </section>

      {/* blog section */}
      {/* <section className="py-10 sm:py-16 lg:pb-24">
        <div className="px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              Latest from blog
            </h2>
            <p className="max-w-xl mx-auto mt-4 text-base leading-relaxed text-gray-400">
              Amet minim mollit non deserunt ullamco est sit aliqua dolor do
              amet sint. Velit officia consequat duis.
            </p>
          </div>

          <div className="grid max-w-md grid-cols-1 mx-auto mt-12 lg:max-w-full lg:mt-16 lg:grid-cols-3 gap-x-8 gap-y-12">
            <div className="transition-all duration-300 transform hover:bg-slate-900/40 p-4">
              <Link href="#" title="" className="block aspect-w-4 aspect-h-3">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn.rareblocks.xyz/collection/celebration/images/blog/1/blog-post-1.jpg"
                  alt=""
                />
              </Link>
              <span className="inline-flex px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-full text-rose-500 bg-rose-600/15 mt-9">
                {" "}
                Technology{" "}
              </span>
              <p className="mt-6 text-xl font-semibold">
                <Link href="#" title="" className="">
                  {" "}
                  How to mange your remote team?{" "}
                </Link>
              </p>
              <p className="mt-4 text-gray-400">
                Amet minim mollit non deserunt ullamco est sit aliqua dolor do
                amet sint. Velit officia consequat duis enim velit mollit.
              </p>
              <div className="h-0 mt-6 mb-4 border-t-2 border-gray-200 border-dashed"></div>
              <span className="block text-sm font-bold tracking-widest text-gray-500 uppercase">
                {" "}
                Martin Jones . June 12, 2021{" "}
              </span>
            </div>

            <div className="transition-all duration-300 transform hover:bg-slate-900/40 p-4">
              <Link href="#" title="" className="block aspect-w-4 aspect-h-3">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn.rareblocks.xyz/collection/celebration/images/blog/1/blog-post-2.jpg"
                  alt=""
                />
              </Link>
              <span className="inline-flex px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-full text-sky-500 bg-sky-600/15 mt-9">
                {" "}
                Marketing{" "}
              </span>
              <p className="mt-6 text-xl font-semibold">
                <Link href="#" title="" className="">
                  {" "}
                  6 Product launching emails you want to use on next campaign.{" "}
                </Link>
              </p>
              <p className="mt-4 text-gray-400">
                Amet minim mollit non deserunt ullamco est sit aliqua dolor do
                amet sint. Velit officia consequat duis enim velit mollit.
              </p>
              <div className="h-0 mt-6 mb-4 border-t-2 border-gray-200 border-dashed"></div>
              <span className="block text-sm font-bold tracking-widest text-gray-500 uppercase">
                {" "}
                Martin Jones . June 12, 2021{" "}
              </span>
            </div>

            <div className="transition-all duration-300 transform hover:bg-slate-900/40 p-4">
              <Link href="#" title="" className="block aspect-w-4 aspect-h-3">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn.rareblocks.xyz/collection/celebration/images/blog/1/blog-post-3.jpg"
                  alt=""
                />
              </Link>
              <span className="inline-flex px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-full text-amber-500 bg-amber-600/15 mt-9">
                {" "}
                Marketing{" "}
              </span>
              <p className="mt-6 text-xl font-semibold">
                <Link href="#" title="" className="">
                  {" "}
                  Learn from the best: 7 email marketing ideas you can use{" "}
                </Link>
              </p>
              <p className="mt-4 text-gray-400">
                Amet minim mollit non deserunt ullamco est sit aliqua dolor do
                amet sint. Velit officia consequat duis enim velit mollit.
              </p>
              <div className="h-0 mt-6 mb-4 border-t-2 border-gray-200 border-dashed"></div>
              <span className="block text-sm font-bold tracking-widest text-gray-500 uppercase">
                {" "}
                Martin Jones . June 12, 2021{" "}
              </span>
            </div>
          </div>
        </div>
      </section> */}

      <div
        ref={imageRef}
        className="hidden lg:block acene"
        style={{
          position: "fixed", // Fixed to the viewport
          top: "13rem",
          left: "22rem",
          zIndex: -1,
          // default
          // top: "12.5rem",
          // left: "21rem",
          // golden visa
          // top: "11rem",
          // left: "33rem",
        }}
      >
        <GlobeDemo />
      </div>

      {/* background colors animations */}
      {/* <div className="pointer-events-none absolute inset-0 h-16 w-full before:absolute before:bottom-[-20%] before:left-1/2 before:z-[0] before:h-[80%] before:w-[60%] before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:bg-[length:200%] before:opacity-20 before:[filter:blur(calc(4*1rem))]"></div> */}

      <div className="pointer-events-none absolute inset-0 h-16 w-full hidden lg:block">
        <div className="absolute bottom-[-20%] left-1/2 z-[0] h-[80%] w-[80%] -translate-x-1/2 animate-rainbow bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] opacity-20 [filter:blur(4rem)]"></div>
      </div>
    </motion.div>
  );
};

export default Home;
